import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import { T } from '@sm/intl';
import {
  OpenEndedAnswerType,
  RespondentCapabilityProvider,
  ExternalQuestion,
} from '@sm/question-widgets/respondent-survey';
import classNames from 'classnames';
import { BypassBlock } from '~app/components/core/BypassBlock';
import { ExitLink } from '~app/components/Survey/ExitLink';
import { SurveyTitle } from '~app/components/Survey/SurveyTitleV2';
import createHeadingLevel from './helper/createHeadingLevel';
import SurveyFooter from '~app/components/Survey/FooterContainer';
import { PageTitle } from '~app/components/Survey/PageTitle';
import { PageDescription } from '~app/components/Survey/PageDescription';
import { SurveyButton } from '~app/components/Survey/SurveyButton';
import useLayoutEffect from '~app/hooks/useIsomorphicLayoutEffect';
import { Layout } from './components/Layout';
import { QuestionList } from './components/QuestionList';
import { PersistentProgressBar } from '~app/components/Survey/PersistentProgressBar';
import ModifiedSurveyDialog from '~app/components/Survey/ModifiedSurveyDialog';
import extrapolateSurveyData from './helper/extrapolateSurveyData';
import useStyles from './useStyles';
import { SurveyContext } from './SurveyContext';
import { CurrentPageContext } from './CurrentPageContext';
import { Logo } from './components/Logo';
import useCenterContainerOffset from './hooks/useCenterContainerOffset';
import { PageNavigation } from './components/PageNavigation';
import { SurveyFormatProvider, useSurveyFormat, SurveyFormatView } from '~app/components/Survey/SurveyFormat';
import { useSurveyErrorsContext } from './errors';

const COPY = {
  SKIP_LINK_CONTENT: {
    id: 'SurveyTaking.SkipLinkContent',
    defaultMessage: 'Skip to content',
    description: '[Type: label][Vis: med] - Skip to content link label (Accessibility)',
  },
};

const Survey: React.FC = () => {
  const { survey, collector, activePage, nextPage, previousPage, submitSurvey } = useContext(SurveyContext);
  const { questions = [], answers = {}, totalAnswers, updateAnswer, validatePage } = useContext(CurrentPageContext);
  const formatContext = useSurveyFormat({ format: survey?.format });

  const { hasVersionChanged, hasValidationErrors, getErrorsByType, setErrors, errorGroups } = useSurveyErrorsContext();

  const handlePageReload = useCallback(() => window.location.reload(), []);

  /** The questions to be used by QuestionList */
  const mappedQuestions = useMemo(
    () =>
      questions.map(
        q =>
          ({
            ...q,
            questionBank: null,
            errors: [],
            disabled: false,
            questionNumber: q.position.inSurvey,
            title: q.heading.default.text,
            typeId: 'single_textbox',
            // Something here is broken/incompatible at the moment (rchang)
          } as unknown as ExternalQuestion)
      ),
    [questions]
  );

  const layoutContainerRef = React.useRef<HTMLDivElement>(null);
  const pageTitleRef = React.useRef<HTMLDivElement>(null);
  const persistentProgressRef = React.useRef<HTMLDivElement>(null);
  const partialContainerRef = React.useRef<HTMLDivElement>(null);
  const logoContainerRef = React.useRef<HTMLDivElement>(null);
  const toolbarContainerRef = React.useRef<HTMLDivElement>(null);
  const topContainerRef = React.useRef<HTMLDivElement>(null);
  const mainContainerRef = React.useRef<HTMLDivElement>(null);
  const pageNavigationRef = React.useRef<HTMLDivElement>(null);
  const surveyFooterRef = React.useRef<HTMLDivElement>(null);
  const mainRef = React.useRef<HTMLDivElement>(null);

  const offsetTop = useCenterContainerOffset(
    formatContext.views[0],
    mainContainerRef,
    [persistentProgressRef],
    [topContainerRef],
    (margin: number) => {
      if (hasVersionChanged) return margin;
      if (survey?.design?.theme?.version === 'V3') {
        const cr = window.getComputedStyle(mainContainerRef?.current as Element);
        return Math.abs(margin / 2 - parseInt(cr.paddingTop, 10));
      }
      return margin;
    }
  );

  /**
   * calculateOffsetBottom
   * Calculate bottom margin for the SurveyFooter. This ensures there is
   * enough room for footer links and last question can move to centre page.
   */
  const [offsetBottom, setOffsetBottom] = useState(0);
  useLayoutEffect(() => {
    const calculateOffsetBottom = (): void => {
      const footerHeight = surveyFooterRef.current?.offsetHeight ?? 0;
      const progressHeight = persistentProgressRef.current?.offsetHeight ?? 0;
      const offset = footerHeight + progressHeight;
      setOffsetBottom(survey?.design?.footer?.enabled ? window.innerHeight / 2 - offset : offset);
    };
    window.addEventListener('resize', calculateOffsetBottom);
    calculateOffsetBottom(); // run immediately
    return () => window.removeEventListener('resize', calculateOffsetBottom);
  }, [survey?.design?.footer?.enabled, surveyFooterRef, persistentProgressRef, setOffsetBottom]);

  const focusFirstQuestionError = useCallback(() => {
    const errors = getErrorsByType(errorGroups.validation);
    const firstError = errors?.[0]?.questionId || null;
    if (firstError) {
      formatContext.setFocus(firstError);
    }
  }, [getErrorsByType, formatContext, errorGroups]);

  const surveyDone = useCallback(
    async (submitSurveyFn: Function) => {
      const isValid = validatePage();

      // TODO: If we want to track first_submit for one page surveys, that logic should go here.

      if (isValid) {
        const result = await submitSurveyFn(answers);
        const { submitSurveyResponse } = result.data;
        if (submitSurveyResponse?.success) {
          // _manageSurveyTakingCookies();
          Cookies.set(`RP_${collector?.id}`, submitSurveyResponse?.encryptedSmParam || '');
          // _redirector();
          // use replace to avoid going back to survey using back button
          window.location.replace(`/survey-thanks/?sm=${submitSurveyResponse?.encryptedSmParam || ''}`);
        } else {
          const errors = submitSurveyResponse?.userErrors;
          setErrors(errs => [...errs, ...errors]);
        }
      } else {
        focusFirstQuestionError();
      }
    },
    [answers, validatePage, collector, focusFirstQuestionError, setErrors]
  );

  useEffect(() => {
    if (hasValidationErrors) {
      focusFirstQuestionError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidationErrors]);

  const {
    surveyTitleContainer,
    pageHeaderContainer,
    layoutMainContainer,
    questionContainer,
    pageFooterContainer,
    sronlyFocus,
    pageNavigationContainer,
    surveyFooterContainer,
  } = useStyles({ offsetTop, offsetBottom });

  if (!survey || Object.entries(survey).length === 0) {
    return null;
  }

  const {
    isSurveyTitleEnabled,
    showPageTitles,
    isFooterEnabled,
    isAutoScroll,
    isProgressBarEnabled,
    totalQuestionsCount,
    isQuestionNumberingEnabled,
    showRequiredQuestionAsterisks,
    surveyTitleAlignment,
    labels,
    totalPages,
    logo: { enabled: isLogoEnabled, ...logoProps },
  } = extrapolateSurveyData(survey);

  const surveyTitleHTML = survey?.titleHTML;

  const surveyId = survey?.id;
  /**
   * Page Header
   * calculate proper Page Title and Description based on current page
   */
  const hasPageDescription = !!activePage?.subHeading;
  const pageDescription = activePage?.subHeading;
  const pageTitle = activePage?.heading;
  /**
   * Skip to content will attempt to move the focus position (without actually focusing)
   * to the Survey Title (if exists), then Page title (if exists), finally to the
   * main-content container (<main>).
   * `tabIndex={-1}` is added to each element (backwards compat with <= IE 11)
   */
  const isExitLinkEnabled = true;
  const surveyTitleId = `survey-title-${surveyId}`;
  const pageTitleId = `page-title-${surveyId}`;
  const mainContentId = `main-content-${surveyId}`;

  const currentPage = activePage?.position ?? 1;

  let skipContentId = mainContentId;
  if (showPageTitles && pageTitle) {
    skipContentId = pageTitleId;
  }
  const nextHeadingElement = createHeadingLevel();

  const layoutContainerOptions = {
    layoutContainer: { ref: layoutContainerRef },
    partialContainer: { ref: partialContainerRef },
    topContainer: { ref: topContainerRef },
    mainContainer: { className: layoutMainContainer, ref: mainContainerRef },
    logoContainer: { ref: logoContainerRef },
    toolbarContainer: { ref: toolbarContainerRef },
  };

  const settings = {
    survey: {
      numberingEnabled: isQuestionNumberingEnabled,
      asteriskEnabled: showRequiredQuestionAsterisks,
    },
  };

  const upstreamActions = {
    onLeaveQuestion: (externalQuestion: ExternalQuestion, answer: OpenEndedAnswerType) => {
      if (questions) {
        updateAnswer(externalQuestion, answer);
      }
    },
  };

  return (
    <SurveyFormatProvider {...formatContext}>
      <BypassBlock>
        <a href={`#${skipContentId}`}>
          <T desc={COPY.SKIP_LINK_CONTENT} />
        </a>
      </BypassBlock>
      {hasVersionChanged && <ModifiedSurveyDialog onConfirmClick={handlePageReload} />}
      <Layout
        logo={!!isLogoEnabled && <Logo {...logoProps} />}
        tools={!!isExitLinkEnabled && <ExitLink href="#" label={labels.exit} />}
        options={layoutContainerOptions}
      >
        <main id={mainContentId} ref={mainRef}>
          <div id={`survey-title-container-${surveyId}`} className={surveyTitleContainer}>
            {isSurveyTitleEnabled && (
              <SurveyTitle
                id={surveyTitleId}
                align={surveyTitleAlignment}
                element={nextHeadingElement(0)}
                text={surveyTitleHTML || ''}
              />
            )}
            {/* <SurveyIntro text={surveyIntro} /> */}
            {/* <SurveyButton translate="no" size="md">OK</SurveyButton> */}
          </div>

          <header id={`page-header-container-${surveyId}`} className={pageHeaderContainer}>
            {!!(showPageTitles && pageTitle) && (
              <PageTitle id={pageTitleId} element={nextHeadingElement(1)} text={pageTitle || ''} tabIndex={-1} />
            )}

            {hasPageDescription && (
              <SurveyFormatView name="pageDescription" ref={pageTitleRef} noShield>
                {(isActive, setNextActive) => (
                  <>
                    <PageDescription text={pageDescription || ''} />
                    {isAutoScroll && (
                      <SurveyButton
                        size="md"
                        translate="no"
                        onClick={setNextActive}
                        className={classNames({ [sronlyFocus]: !isActive })}
                      >
                        OK
                      </SurveyButton>
                    )}
                  </>
                )}
              </SurveyFormatView>
            )}
          </header>

          <RespondentCapabilityProvider settings={settings} upstreamActions={upstreamActions}>
            <form id={`form-${surveyId}`} className={questionContainer}>
              {/**
               * NOTE: active (boolean) will be controlled by Progression Engine
               * NOTE: Are all the nullified fields (questionBank, validation) needed?
               */}
              <QuestionList answers={answers} questions={mappedQuestions} />
              {/**
               *  When survey is in OQAAT mode, progress bar is below the survey footer and is sticky
               */}
              {!!isProgressBarEnabled && isAutoScroll && (
                <PersistentProgressBar value={totalAnswers} total={totalQuestionsCount} ref={persistentProgressRef} />
              )}
            </form>
          </RespondentCapabilityProvider>
          <footer id={`page-footer-container-${surveyId}`} className={pageFooterContainer}>
            <PageNavigation
              ref={pageNavigationRef}
              className={pageNavigationContainer}
              labels={labels}
              currentPage={currentPage}
              totalPages={totalPages ?? 1}
              onNext={nextPage}
              onPrevious={previousPage}
              onDone={async () => surveyDone(submitSurvey)}
            />
          </footer>
        </main>
        <footer id={`survey-footer-container-${surveyId}`} className={surveyFooterContainer} ref={surveyFooterRef}>
          {isFooterEnabled && (
            <SurveyFooter
              element="div"
              isGrayScale={!!survey?.design?.footer?.grayscale}
              showPrivacyCookiePolicy
              // Below are placeholders, fill in when we get info from query
              showReportProblem={false}
              reportProblemURL=""
              isGoogleSubdomain={false}
              googlePrivacyPolicyURL=""
            />
          )}
        </footer>
      </Layout>
    </SurveyFormatProvider>
  );
};

export default Survey;
